import React, { useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';
import { isCompatible, PROJECT_FILE_FORMAT_VERSION } from '../core/projectFileAPI';
import { useDispatch, useSelector } from 'react-redux';
import QuickSearch from './QuickSearch';
import EditorsPane from './EditorsPane';
import Flex from './layouts/Flex';
import { css } from '@emotion/css';
import { COLOR_BG_SECONDARY, COLOR_BORDER_PRIMARY, COLOR_TEXT_PRIMARY } from './theme';
import { useServices } from './ServiceProvider';
import AppHeader from './AppHeader';
import NavBar from './NavBar';
import AppWideDialogs from './AppWideDialogs';
import AppWideNotifications from './AppWideNotifications';
const darkTheme = createTheme({
    palette: {
        mode: 'dark',
    },
    typography: {
        fontSize: 16,
    }
});
const layoutStyle = css `
  color: ${COLOR_TEXT_PRIMARY};
  overflow: hidden;
  height: 100vh;
`;
const menuBarStyle = css `
  background-color: ${COLOR_BG_SECONDARY};
  border-bottom: 1px solid ${COLOR_BORDER_PRIMARY};
  padding: 0.33em;
`;
export const App = ({ platformAPI }) => {
    const { ChangeService, EditorsService, NotificationsService, ProjectService, ResourceTreeService } = useServices();
    const project = useSelector(ProjectService.selectors.getProject);
    const projectFilePath = useSelector(ProjectService.selectors.getProjectFilePath);
    const dispatch = useDispatch();
    const focusedResourceID = useSelector((s) => EditorsService.getFocusedTab(s)?.resource?.id);
    const nextUndo = useSelector((s) => ChangeService.getUndoSnapshot(s, focusedResourceID));
    const nextRedo = useSelector((s) => ChangeService.getRedoSnapshot(s, focusedResourceID));
    // @TODO create a KeybindsService whose job it is to accept and
    // register keybind actions, with other services contributing
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'o' && e.ctrlKey) {
                e.stopPropagation();
                e.preventDefault();
                platformAPI.requestOpenProject();
            }
            if (e.key === 's' && e.ctrlKey) {
                e.stopPropagation();
                e.preventDefault();
                platformAPI.requestSave(project, projectFilePath);
            }
            if (e.key === 'y' && e.ctrlKey) {
                e.stopPropagation();
                e.preventDefault();
                dispatch(ResourceTreeService.actions.toggleVisible());
            }
            if (e.key === 'z' && e.ctrlKey && !e.shiftKey) {
                e.stopPropagation();
                e.preventDefault();
                if (Boolean(nextUndo))
                    dispatch(ChangeService.undo(focusedResourceID));
            }
            if (e.key === 'Z' && e.ctrlKey) {
                e.stopPropagation();
                e.preventDefault();
                if (Boolean(nextRedo))
                    dispatch(ChangeService.redo(focusedResourceID));
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [project, nextUndo, nextRedo]);
    // @TODO move this listener registration to the `onStart` service method
    // of the service that requests opening
    useEffect(() => {
        platformAPI.onOpenProject((project, filePath) => {
            if (!isCompatible(project)) {
                throw new Error(`FileFormatMismatch; Project ${filePath}; expected fileFormatVersion ${PROJECT_FILE_FORMAT_VERSION} but was ${project?.fileFormatVersion}`);
            }
            dispatch(ProjectService.actions.openProject({ project, filePath }));
        });
    });
    useEffect(() => {
        const errorHandler = (e) => {
            e.preventDefault?.();
            e.stopPropagation?.();
            console.error('UnhandledError', e);
            dispatch(NotificationsService.createNotification({
                level: 'error',
                title: 'Something went wrong',
                body: JSON.stringify(e.error),
            }));
        };
        const promiseRejectionHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            console.error('UnhandledPromiseRejection', e);
            dispatch(NotificationsService.createNotification({
                level: 'error',
                title: 'A background task has failed',
                body: JSON.stringify(e.reason),
            }));
        };
        window.addEventListener('error', errorHandler);
        window.addEventListener('unhandledrejection', promiseRejectionHandler);
    });
    return (React.createElement("div", { id: "app" },
        React.createElement(ThemeProvider, { theme: darkTheme },
            React.createElement(AppWideDialogs, null),
            React.createElement(AppWideNotifications, null),
            React.createElement(QuickSearch, null),
            React.createElement(Flex, { direction: "column", className: layoutStyle },
                React.createElement(Flex, { className: menuBarStyle, alignItems: "center" },
                    React.createElement(AppHeader, null)),
                React.createElement(Flex, { className: css({ flexGrow: '1' }) },
                    React.createElement(NavBar, null),
                    React.createElement(EditorsPane, { className: 'foo' }))))));
};
//# sourceMappingURL=App.js.map